import { useState, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  Alert,
  KeyboardAvoidingView,
  ScrollView,
  Platform,
  TouchableOpacity,
} from "react-native";
import { useTranslation } from "react-i18next";
import { useNavigation } from "@react-navigation/native";
import { Feather } from "@expo/vector-icons";
import { useColors, ThemeColors } from "../../hooks/useColors";
import { spacing, typography } from "../../theme";

import { Input } from "../../components/ui/Input";
import { Button } from "../../components/ui/Button";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../store";
import {
  forgotPasswordRequest,
  resetForgotPasswordState,
} from "../../store/slices/authSlice";
import { Card } from "../../components/ui/Card";

export default function ForgotPasswordScreen() {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const dispatch = useDispatch<AppDispatch>();
  const colors = useColors();
  const styles = createStyles(colors);
  const [email, setEmail] = useState("");
  const [remainingTime, setRemainingTime] = useState(0);

  const {
    loading,
    forgotPasswordComplete,
    passwordResetCooldown,
    lastPasswordResetRequest,
  } = useSelector((state: RootState) => state.auth);

  const handleSubmit = () => {
    if (!email) {
      Alert.alert(t("auth.error"), t("auth.enterEmail"));
      return;
    }
    dispatch(forgotPasswordRequest({ email }));
  };

  useEffect(() => {
    if (forgotPasswordComplete) {
      // Reset the forgotPasswordComplete state before navigation
      dispatch(resetForgotPasswordState());
      //@ts-ignore
      navigation.navigate("SignUpConfirmation");
    }
  }, [forgotPasswordComplete, navigation, dispatch]);

  // Update remaining time for cooldown
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;

    if (passwordResetCooldown && lastPasswordResetRequest) {
      const updateRemainingTime = () => {
        const timeSinceLastRequest = Date.now() - lastPasswordResetRequest;
        const cooldownPeriod = 2 * 60 * 1000; // 2 minutes in milliseconds
        const remaining = Math.max(0, cooldownPeriod - timeSinceLastRequest);

        if (remaining > 0) {
          setRemainingTime(Math.ceil(remaining / 1000));
        } else {
          setRemainingTime(0);
        }
      };

      updateRemainingTime();
      interval = setInterval(updateRemainingTime, 1000);
    } else {
      setRemainingTime(0);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [passwordResetCooldown, lastPasswordResetRequest]);

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === "ios" ? "padding" : "height"}
    >
      <ScrollView
        contentContainerStyle={styles.scrollContainer}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.contentContainer}>
          <TouchableOpacity
            onPress={() => navigation.goBack()}
            style={styles.backButton}
          >
            <Feather name="arrow-left" size={24} color={colors.text} />
          </TouchableOpacity>
          <Text style={styles.title}>{t("auth.forgotPasswordTitle")}</Text>
          <Text style={styles.subtitle}>{t("auth.enterEmailToReset")}</Text>

          <Card>
            <Input
              label={t("auth.email")}
              value={email}
              onChangeText={setEmail}
              autoCapitalize="none"
              keyboardType="email-address"
              iconName="mail-outline"
              autoCorrect={false}
            />

            {remainingTime > 0 && (
              <Text style={styles.cooldownText}>
                Please wait {Math.floor(remainingTime / 60)}:
                {String(remainingTime % 60).padStart(2, "0")} before requesting
                another reset email.
              </Text>
            )}

            <Button
              onPress={handleSubmit}
              loading={loading}
              disabled={remainingTime > 0}
              style={styles.bottomButton}
            >
              {remainingTime > 0
                ? `Resend in ${Math.floor(remainingTime / 60)}:${String(
                    remainingTime % 60
                  ).padStart(2, "0")}`
                : t("auth.sendResetLink")}
            </Button>
          </Card>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    scrollContainer: {
      flexGrow: 1,
      justifyContent: "center",
    },
    contentContainer: {
      padding: spacing.md,
    },
    backButton: {
      alignSelf: "flex-start",
      marginBottom: spacing.md,
      padding: spacing.xs,
    },
    title: {
      ...typography.title,
      fontSize: 22,
      marginBottom: spacing.sm + 4,
      textAlign: "center",
      color: colors.text,
    },
    subtitle: {
      ...typography.body,
      marginBottom: spacing.lg,
      textAlign: "center",
      color: colors.textLight,
    },
    bottomButton: {
      marginTop: spacing.md,
    },
    cooldownText: {
      ...typography.body,
      fontSize: 14,
      color: colors.error,
      textAlign: "center",
      marginTop: spacing.sm,
      marginBottom: spacing.sm,
    },
  });
